import { FC, useContext, useEffect, useState, } from "react";
import { useParams } from "react-router-dom";

import CaseInformation from "src/components/caseInformation";
import CaseNotes from "src/components/caseNotes";
import DocumentUpload from "src/components/documentUpload";
import FooterText from "src/components/footerText";
import { CaseNotesType } from "src/components/caseNotes/types";
import InsuranceInformation from "src/components/insuranceInformation";
import PatientInformation from "src/components/patientInformation";
import PrescriberInformation from "src/components/prescriberInformation";

import useFetch from "src/hooks/useFetch";
import { LoaderContext } from "src/context/loaderContext";

import { CaseDetailType } from '../../../src/types/caseDetail';
import { CaseDetailAPIResponseType } from "./types";
import { APIEndpoints } from "src/types/apiTypes";
import AuthSubHeader from "../../components/authSubHeader";
import PracticeInformation from "src/components/practiceInformation";
import ReferringPrescriberAndProviderInformation from "src/components/referingPrescriberAndPracticeInformation";
import CopayInformation from "src/components/copayInformation";


const HubCaseDetail: FC = (): JSX.Element => {
    const { caseId } = useParams();
    const { hideLoader } = useContext(LoaderContext);
    const [caseNotes, setCaseNotes] = useState<CaseNotesType[]>([]);
    const [caseDetail, setCaseDetail] = useState<CaseDetailType>();
    const [is_enrolled_in_federal, update_is_enrolled_in_federal] = useState<boolean>()

    const { data: caseDetailObject } =
        useFetch<CaseDetailAPIResponseType>(APIEndpoints.CASE_DETAIL, {}, { caseId });

    useEffect(() => {
        if (caseDetailObject) {
            hideLoader();
            setCaseNotes(caseDetailObject?.data.case_notes)
            setCaseDetail(caseDetailObject?.data);
            if (caseDetailObject && caseDetailObject.data && caseDetailObject.data.patient_information) {
                const { patient_information } = caseDetailObject.data
                const is_enrolled_in_federal = patient_information.patient_attributes.find((item) => item.name === "is_enrolled_in_federal")
                if (is_enrolled_in_federal) {
                    if (is_enrolled_in_federal.value === "true") update_is_enrolled_in_federal(true)
                    else update_is_enrolled_in_federal(false)
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseDetailObject]);





    return (
        <div className="overflow-y-auto overflow-x-hidden d-flex flex-column flex-fill">
            <div className="bg-mist py-2">
                <div className="container-fluid">
                    <AuthSubHeader />
                </div>
            </div>
            <div className="container-fluid pt-2 d-flex flex-column flex-fill case-detail-label">
                <div className="row mb-3">
                    <div className="col-sm-8 col-12">
                        <h2 className="text-primary-grey m-0 flex-grow-1 mb-2">Case#
                            <span className="badge bg-primary ms-2 rounded-5">{caseId}</span>
                        </h2>
                    </div>
                    <div className="col-sm-4 col-12">
                        <div
                            className={`text-center text-white fs-4 rounded-3 background-color-${caseDetail && caseDetail.case_information ? caseDetail.case_information.color : 1}`}>
                            {caseDetail && caseDetail.case_information ? caseDetail.case_information.status : ''}
                        </div>
                    </div>
                </div>

                {caseDetail ? (
                    <>
                        {/* Case Information */}
                        <div className="">
                            <CaseInformation caseObject={caseDetail} />
                        </div>
                        {/* Patient Information */}
                        <div>
                            <PatientInformation caseObject={caseDetail} />
                        </div>
                        {/* Prescriber Information */}
                        {caseDetail.case_information.product === "ZILRETTA" ? (
                            <div>
                                <PrescriberInformation prescriber_information={caseDetail.prescriber_information}
                                    title="Prescriber Information"
                                    drugName={caseDetail.case_information.product} />
                            </div>
                        ) : (
                            <>
                                {caseDetail.case_information.treating_provider_as_practice ? (
                                    <div>
                                        <PracticeInformation
                                            practice_information={caseDetail.case_information.treating_provider_as_practice}
                                            title="Treating Prescriber Information" />
                                    </div>
                                ) : (
                                    <div>
                                        <PrescriberInformation
                                            prescriber_information={caseDetail.case_information.treating_prescriber || caseDetail.prescriber_information}
                                            title="Treating Prescriber Information"
                                            drugName={caseDetail.case_information.product} />
                                    </div>
                                )}
                                <div>
                                    <PracticeInformation
                                        practice_information={caseDetail.case_information.treating_practice || caseDetail.practice_information}
                                        title="Treating Facility Information" />
                                </div>
                                {caseDetail.case_information.referring_practice ? (
                                    <ReferringPrescriberAndProviderInformation
                                        practice_information={caseDetail.case_information.referring_practice}
                                        prescriber_information={caseDetail.case_information.referring_prescriber}
                                        title={'Referring Prescriber and Facility Information'} />
                                ) : null}
                            </>
                        )}
                        

                        {/* Insurance Information */}
                        <div className="card">
                            <div
                                className={`card-header ${caseDetail.case_information.product === 'iovera°' ? 'iovera-theme' : 'zilretta-theme'}`}>
                                <h4 className="m-0">Insurance Information</h4>
                            </div>

                            <div className="card-body">
                                {typeof is_enrolled_in_federal === "boolean" && caseDetail.case_information.product.toLowerCase().includes("zilr") ? (
                                    <div className="row align-items-center">
                                        <div className="col-10 px-3 d-flex justify-content-center align-items-center">
                                            <span>
                                                Is the patient enrolled in a federal- or state-funded healthcare program
                                                such as Medicare, Medicare Advantage, Medicaid, VA, DoD, TRICARE, a
                                                qualified health plan (QHP), or any other federal or state healthcare
                                                plan or a pharmaceutical assistance program?
                                            </span>
                                            <span className="ms-1">:</span>
                                        </div>
                                        <div className="col-2 text-start">
                                            {is_enrolled_in_federal ? "Yes" : "No"}
                                        </div>
                                    </div>
                                ) : null}
                              

                                {caseDetail.insurance_information.length > 0 ?
                                    (
                                        <>
                                            {caseDetail.insurance_information.map((item, index) => (
                                                <InsuranceInformation
                                                    drugName={caseDetail.case_information.product} key={index}
                                                    insuranceNumber={index + 1}
                                                    insuranceInformation={item}
                                                />
                                            ))}
                                        </>
                                    )
                                    : null}
                            </div>
                        </div>
                        {/* {Copay Information} */}
                          {caseDetail.case_information.product === "ZILRETTA" &&
                                    
                                <CopayInformation copay_information={caseDetail.copay_program} />
                                    
                                }
                        {/* Documents */}
                        <div>
                            <DocumentUpload
                                setNotes={setCaseNotes}
                                caseDocuments={caseDetail.case_documents ? caseDetail.case_documents : []}
                                caseId={caseDetail.case_information ? caseDetail.case_information.case_id : 0}
                                drugName={caseDetail.case_information.product}
                            />
                        </div>
                        {/* Case Notes */}
                        <div>
                            <CaseNotes caseNotes={caseNotes} setNotes={setCaseNotes} caseObject={caseDetail}
                                drugName={caseDetail.case_information.product} />
                        </div>
                    </>
                ) : null}
            </div>
            <FooterText footerType="black" />
        </div>
    );
};

export default HubCaseDetail;
